import type { ActionFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import { z } from 'zod';
import { deleteMessage } from '~/modules/chat/api/delete-message.server';

export const DELETE_MESSAGE_API = '/remix-api/chat/delete-message';

const FORM_DATA_SCHEMA = z.object({
    messageID: z.coerce.number().int().positive(),
});

/**
 * API endpoint to delete a message from a conversation.
 * Use with the `useConversationMessages` hook.
 * @see {@link packages/web/app/modules/chat/hooks/use-conversation-messages.ts}
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { logger } = context;
    const payload = await request.json();
    const result = FORM_DATA_SCHEMA.safeParse(payload);

    if (!result.success) {
        return json(null, { status: 400 });
    }

    try {
        const response = await deleteMessage({
            context,
            messageID: result.data.messageID,
        });

        if (!response) {
            return json(null, { status: 500 });
        }

        let statusCode = 200;
        if (
            response.__typename === 'NotAuthorizedError' ||
            response.__typename === 'NotFoundError'
        ) {
            statusCode = 403;
        } else if (response.__typename === 'UserError') {
            statusCode = 400;
        } else if (response.__typename === 'ServerError') {
            statusCode = 500;
        }

        return json({ response }, { status: statusCode });
    } catch (error) {
        logger.error(`Error in ${DELETE_MESSAGE_API}`, { error });
        return json(null, { status: 500 });
    }
};
